import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Eye,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Video,
  FileText,
  Clock,
  Loader2,
  Settings,
  Image,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import {
  useCourse,
  useCreateCourse,
  useUpdateCourse,
  useCreateModule,
  useUpdateModule,
  useDeleteModule,
  useCreateLesson,
  useUpdateLesson,
  useDeleteLesson,
} from '@/api/hooks';
import type { Module, Lesson, Course } from '@/api/client';

export const Route = createFileRoute('/author/courses/$courseId/edit')({
  component: CourseEditorPage,
});

interface EditableModule extends Module {
  isExpanded?: boolean;
  isNew?: boolean;
}

interface EditableLesson extends Lesson {
  isNew?: boolean;
}

interface EditableCourse {
  name: string;
  description: string;
  thumbnail_url: string;
  pillar: string;   // was: category (renamed in migration 085)
  status: string;
  estimated_hours: number | null;
  modules: EditableModule[];
}

function CourseEditorPage() {
  const { courseId } = Route.useParams();
  const navigate = useNavigate();
  const isNew = courseId === 'new';

  const { data: existingCourse, isLoading, refetch } = useCourse(isNew ? '' : courseId);

  // Mutations
  const createCourseMutation = useCreateCourse();
  const updateCourseMutation = useUpdateCourse();
  const createModuleMutation = useCreateModule();
  const updateModuleMutation = useUpdateModule();
  const deleteModuleMutation = useDeleteModule();
  const createLessonMutation = useCreateLesson();
  const updateLessonMutation = useUpdateLesson();
  const deleteLessonMutation = useDeleteLesson();

  const [course, setCourse] = useState<EditableCourse>({
    name: '',
    description: '',
    thumbnail_url: '',
    pillar: '',
    status: 'draft',
    estimated_hours: null,
    modules: [],
  });

  const [savedCourseId, setSavedCourseId] = useState<string | null>(isNew ? null : courseId);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Load existing course data
  useEffect(() => {
    if (existingCourse && !isNew) {
      setCourse({
        name: existingCourse.name || '',
        description: existingCourse.description || '',
        thumbnail_url: existingCourse.thumbnail_url || '',
        pillar: existingCourse.pillar || '',
        status: existingCourse.status || 'draft',
        estimated_hours: existingCourse.estimated_hours ?? null,
        modules: (existingCourse.modules || []).map((m) => ({
          ...m,
          isExpanded: false,
        })),
      });
    }
  }, [existingCourse, isNew]);

  // Save course and all nested content
  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage(null);

    try {
      let currentCourseId = savedCourseId;

      // Step 1: Create or update the course
      if (isNew && !savedCourseId) {
        const newCourse = await createCourseMutation.mutateAsync({
          name: course.name,
          description: course.description || null,
          thumbnail_url: course.thumbnail_url || null,
          pillar: course.pillar || null,
          status: course.status,
          estimated_hours: course.estimated_hours,
          duration_minutes: null,
          access_level: null,
          video_url: null,
          organization_id: null,
        });
        currentCourseId = newCourse.id;
        setSavedCourseId(newCourse.id);
        // Navigate to the edit URL with the real ID
        navigate({ to: '/author/courses/$courseId/edit', params: { courseId: newCourse.id }, replace: true });
      } else if (currentCourseId) {
        await updateCourseMutation.mutateAsync({
          id: currentCourseId,
          updates: {
            name: course.name,
            description: course.description || null,
            thumbnail_url: course.thumbnail_url || null,
            pillar: course.pillar || null,
            status: course.status,
            estimated_hours: course.estimated_hours,
          },
        });
      }

      if (!currentCourseId) {
        throw new Error('Course ID not available');
      }

      // Step 2: Save modules
      for (const mod of course.modules) {
        if (mod.isNew) {
          // Create new module
          const newModule = await createModuleMutation.mutateAsync({
            course_id: currentCourseId,
            name: mod.name,
            description: mod.description,
            order: mod.order,
            status: mod.status as 'draft' | 'published' | 'archived',
          });

          // Create lessons for the new module
          for (const lesson of mod.lessons || []) {
            await createLessonMutation.mutateAsync({
              course_id: currentCourseId,
              module_id: newModule.id,
              name: lesson.name,
              description: lesson.description,
              content: null,
              content_type: lesson.content_type,
              video_url: lesson.video_url,
              video_asset_id: null,
              content_url: lesson.content_url,
              transcript_url: lesson.transcript_url,
              transcript_text: null,
              duration_minutes: lesson.duration_minutes,
              estimated_minutes: null,
              order: lesson.order,
              status: lesson.status,
            });
          }
        } else {
          // Update existing module
          await updateModuleMutation.mutateAsync({
            id: mod.id,
            updates: {
              name: mod.name,
              description: mod.description,
              order: mod.order,
              status: mod.status as 'draft' | 'published' | 'archived',
            },
          });

          // Handle lessons
          for (const lesson of mod.lessons || []) {
            if ((lesson as EditableLesson).isNew || lesson.id.startsWith('new-')) {
              // Create new lesson
              await createLessonMutation.mutateAsync({
                course_id: currentCourseId,
                module_id: mod.id,
                name: lesson.name,
                description: lesson.description,
                content: null,
                content_type: lesson.content_type,
                video_url: lesson.video_url,
                video_asset_id: null,
                content_url: lesson.content_url,
                transcript_url: lesson.transcript_url,
                transcript_text: null,
                duration_minutes: lesson.duration_minutes,
                estimated_minutes: null,
                order: lesson.order,
                status: lesson.status,
              });
            } else {
              // Update existing lesson
              await updateLessonMutation.mutateAsync({
                id: lesson.id,
                updates: {
                  name: lesson.name,
                  description: lesson.description,
                  content_type: lesson.content_type,
                  video_url: lesson.video_url,
                  content_url: lesson.content_url,
                  transcript_url: lesson.transcript_url,
                  duration_minutes: lesson.duration_minutes,
                  order: lesson.order,
                  status: lesson.status,
                },
              });
            }
          }
        }
      }

      // Refetch to get fresh data
      if (!isNew || savedCourseId) {
        await refetch();
      }

      setHasChanges(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Save failed:', error);
      setSaveStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = <K extends keyof EditableCourse>(
    field: K,
    value: EditableCourse[K]
  ) => {
    setCourse((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleAddModule = () => {
    const newModule: EditableModule = {
      id: `new-${Date.now()}`,
      course_id: savedCourseId || courseId,
      name: 'New Module',
      description: '',
      order: course.modules.length,
      status: 'draft',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      lessons: [],
      isExpanded: true,
      isNew: true,
    };
    setCourse((prev) => ({
      ...prev,
      modules: [...prev.modules, newModule],
    }));
    setHasChanges(true);
  };

  const handleModuleChange = (moduleId: string, field: keyof Module, value: unknown) => {
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        m.id === moduleId ? { ...m, [field]: value } : m
      ),
    }));
    setHasChanges(true);
  };

  const handleDeleteModule = async (moduleId: string) => {
    const module = course.modules.find((m) => m.id === moduleId);

    // If it's a saved module (not new), delete from database
    if (module && !module.isNew && !moduleId.startsWith('new-') && savedCourseId) {
      try {
        await deleteModuleMutation.mutateAsync({
          moduleId,
          courseId: savedCourseId,
        });
      } catch (error) {
        console.error('Failed to delete module:', error);
        setErrorMessage('Failed to delete module');
        return;
      }
    }

    // Remove from local state
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.filter((m) => m.id !== moduleId),
    }));
    setHasChanges(true);
  };

  const handleToggleModule = (moduleId: string) => {
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        m.id === moduleId ? { ...m, isExpanded: !m.isExpanded } : m
      ),
    }));
  };

  const handleAddLesson = (moduleId: string) => {
    const newLesson: EditableLesson = {
      id: `new-${Date.now()}`,
      course_id: savedCourseId || courseId,
      module_id: moduleId,
      name: 'New Lesson',
      description: '',
      content: null,
      content_type: 'text',
      video_url: null,
      video_asset_id: null,
      content_url: null,
      transcript_url: null,
      transcript_text: null,
      duration_minutes: null,
      estimated_minutes: null,
      order: course.modules.find((m) => m.id === moduleId)?.lessons?.length || 0,
      status: 'draft',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      isNew: true,
    };
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        m.id === moduleId
          ? { ...m, lessons: [...(m.lessons || []), newLesson] }
          : m
      ),
    }));
    setHasChanges(true);
  };

  const handleLessonChange = (
    moduleId: string,
    lessonId: string,
    field: keyof Lesson,
    value: unknown
  ) => {
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        m.id === moduleId
          ? {
              ...m,
              lessons: m.lessons?.map((l) =>
                l.id === lessonId ? { ...l, [field]: value } : l
              ),
            }
          : m
      ),
    }));
    setHasChanges(true);
  };

  const handleDeleteLesson = async (moduleId: string, lessonId: string) => {
    const module = course.modules.find((m) => m.id === moduleId);
    const lesson = module?.lessons?.find((l) => l.id === lessonId);

    // If it's a saved lesson, delete from database
    if (lesson && !lessonId.startsWith('new-') && savedCourseId) {
      try {
        await deleteLessonMutation.mutateAsync({
          lessonId,
          courseId: savedCourseId,
        });
      } catch (error) {
        console.error('Failed to delete lesson:', error);
        setErrorMessage('Failed to delete lesson');
        return;
      }
    }

    // Remove from local state
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        m.id === moduleId
          ? { ...m, lessons: m.lessons?.filter((l) => l.id !== lessonId) }
          : m
      ),
    }));
    setHasChanges(true);
  };

  if (isLoading && !isNew) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/author"
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isNew ? 'Create Course' : 'Edit Course'}
            </h1>
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              {saveStatus === 'success' && (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-green-500">Saved successfully</span>
                </>
              )}
              {saveStatus === 'error' && (
                <>
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">{errorMessage || 'Save failed'}</span>
                </>
              )}
              {saveStatus === 'idle' && (
                hasChanges ? 'Unsaved changes' : 'All changes saved'
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isNew && savedCourseId && (
            <Link
              to="/learn/courses/$courseId"
              params={{ courseId: savedCourseId }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </Link>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || (!hasChanges && !isNew)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isNew && !savedCourseId ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      {/* Course Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-xl border border-border p-6 mb-6"
      >
        <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Course Details
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Course Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={course.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
              placeholder="Enter course name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Full Description
            </label>
            <textarea
              value={course.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all resize-none"
              placeholder="Detailed course description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Pillar
              </label>
              <select
                value={course.pillar}
                onChange={(e) => handleFieldChange('pillar', e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
              >
                <option value="">Select pillar</option>
                <option value="Leadership">Leadership</option>
                <option value="Finance">Finance</option>
                <option value="Marketing">Marketing</option>
                <option value="Operations">Operations</option>
                <option value="Sales">Sales</option>
                <option value="Strategy">Strategy</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Status
              </label>
              <select
                value={course.status}
                onChange={(e) => handleFieldChange('status', e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Thumbnail URL
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={course.thumbnail_url}
                onChange={(e) => handleFieldChange('thumbnail_url', e.target.value)}
                className="flex-1 px-4 py-2 rounded-lg border border-border bg-secondary/30 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all"
                placeholder="https://..."
              />
              <button className="px-4 py-2 border border-border rounded-lg hover:bg-secondary transition-colors">
                <Image className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Modules */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Course Content</h2>
          <button
            onClick={handleAddModule}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Module
          </button>
        </div>

        <div className="space-y-4">
          {course.modules.map((module, moduleIdx) => (
            <div
              key={module.id}
              className="bg-card rounded-xl border border-border overflow-hidden"
            >
              {/* Module Header */}
              <div className="p-4 flex items-center gap-3">
                <button className="p-1 cursor-grab hover:bg-secondary rounded transition-colors">
                  <GripVertical className="w-4 h-4 text-muted-foreground" />
                </button>

                <button
                  onClick={() => handleToggleModule(module.id)}
                  className="p-1 hover:bg-secondary rounded transition-colors"
                >
                  {module.isExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                <div className="flex-1 flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Module {moduleIdx + 1}</span>
                  <input
                    type="text"
                    value={module.name}
                    onChange={(e) => handleModuleChange(module.id, 'name', e.target.value)}
                    className="flex-1 px-2 py-1 rounded border-0 bg-transparent hover:bg-secondary focus:bg-secondary focus:outline-none font-medium text-foreground"
                  />
                  {module.isNew && (
                    <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded">New</span>
                  )}
                </div>

                <span className="text-sm text-muted-foreground">
                  {module.lessons?.length || 0} lessons
                </span>

                <button
                  onClick={() => handleDeleteModule(module.id)}
                  disabled={deleteModuleMutation.isPending}
                  className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
                >
                  {deleteModuleMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Module Content */}
              {module.isExpanded && (
                <div className="border-t border-border p-4 bg-secondary/20">
                  <div className="mb-4">
                    <label className="block text-sm text-muted-foreground mb-1">
                      Module Description
                    </label>
                    <textarea
                      value={module.description || ''}
                      onChange={(e) =>
                        handleModuleChange(module.id, 'description', e.target.value)
                      }
                      rows={2}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-card focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-all resize-none text-sm"
                      placeholder="Describe what learners will learn in this module"
                    />
                  </div>

                  {/* Lessons */}
                  <div className="space-y-2">
                    {module.lessons?.map((lesson, lessonIdx) => (
                      <div
                        key={lesson.id}
                        className="bg-card rounded-lg border border-border p-3 flex items-center gap-3"
                      >
                        <button className="p-1 cursor-grab hover:bg-secondary rounded transition-colors">
                          <GripVertical className="w-3 h-3 text-muted-foreground" />
                        </button>

                        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                          {lesson.content_type === 'video' ? (
                            <Video className="w-4 h-4 text-red-500" />
                          ) : (
                            <FileText className="w-4 h-4 text-indigo-500" />
                          )}
                        </div>

                        <input
                          type="text"
                          value={lesson.name}
                          onChange={(e) =>
                            handleLessonChange(module.id, lesson.id, 'name', e.target.value)
                          }
                          className="flex-1 px-2 py-1 rounded border-0 bg-transparent hover:bg-secondary focus:bg-secondary focus:outline-none text-sm"
                        />

                        {lesson.id.startsWith('new-') && (
                          <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded">New</span>
                        )}

                        <select
                          value={lesson.content_type || 'text'}
                          onChange={(e) =>
                            handleLessonChange(module.id, lesson.id, 'content_type', e.target.value)
                          }
                          className="px-2 py-1 text-xs rounded border border-border bg-secondary/30"
                        >
                          <option value="text">Text</option>
                          <option value="video">Video</option>
                          <option value="document">Document</option>
                        </select>

                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <input
                            type="number"
                            value={lesson.duration_minutes || ''}
                            onChange={(e) =>
                              handleLessonChange(
                                module.id,
                                lesson.id,
                                'duration_minutes',
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            className="w-12 px-1 py-0.5 rounded border border-border bg-secondary/30 text-center"
                            placeholder="min"
                          />
                        </div>

                        <button
                          onClick={() => handleDeleteLesson(module.id, lesson.id)}
                          disabled={deleteLessonMutation.isPending}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors disabled:opacity-50"
                        >
                          {deleteLessonMutation.isPending ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleAddLesson(module.id)}
                    className="mt-3 flex items-center gap-2 px-3 py-2 text-sm text-indigo-400 hover:bg-indigo-500/20 rounded-lg transition-colors w-full justify-center border border-dashed border-indigo-500/30"
                  >
                    <Plus className="w-4 h-4" />
                    Add Lesson
                  </button>
                </div>
              )}
            </div>
          ))}

          {course.modules.length === 0 && (
            <div className="bg-card rounded-xl border border-dashed border-border p-12 text-center">
              <p className="text-muted-foreground mb-4">
                No modules yet. Add your first module to get started.
              </p>
              <button
                onClick={handleAddModule}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Module
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
